<!-- Toolbar -->
@if (showToolbar()) {
  <div class="hx-json-viewer-toolbar">
    <div class="hx-json-viewer-toolbar-actions">
      @if (viewMode() === 'tree') {
        <button hxButton variant="ghost" size="sm" (click)="expandAll()" aria-label="Expand all nodes">
          <hx-icon svgIcon="arrows-expand" inline="true"></hx-icon>
          <span>Expand</span>
        </button>
        <button hxButton variant="ghost" size="sm" (click)="collapseAll()" aria-label="Collapse all nodes">
          <hx-icon svgIcon="arrows-collapse" inline="true"></hx-icon>
          <span>Collapse</span>
        </button>
      }
      @if (viewMode() === 'edit') {
        <button hxButton variant="ghost" size="sm" (click)="onFormat()" aria-label="Format JSON">
          <hx-icon svgIcon="filter" inline="true"></hx-icon>
          <span>Format</span>
        </button>
      }

      <div class="hx-json-viewer-toolbar-separator"></div>

      <!-- View mode buttons -->
      @if (editable()) {
        <button
          hxButton
          [variant]="viewMode() === 'edit' ? 'soft' : 'ghost'"
          size="sm"
          (click)="setViewMode('edit')"
          aria-label="Edit JSON">
          <hx-icon svgIcon="pencil" inline="true"></hx-icon>
          <span>Edit</span>
        </button>
      }
      <button
        hxButton
        [variant]="viewMode() === 'tree' ? 'soft' : 'ghost'"
        size="sm"
        (click)="setViewMode('tree')"
        aria-label="Show tree view">
        <hx-icon svgIcon="arrows-expand" inline="true"></hx-icon>
        <span>Tree</span>
      </button>
      <button
        hxButton
        [variant]="viewMode() === 'raw' ? 'soft' : 'ghost'"
        size="sm"
        (click)="setViewMode('raw')"
        aria-label="Show raw JSON">
        <hx-icon svgIcon="code" inline="true"></hx-icon>
        <span>Raw</span>
      </button>

      <div class="hx-json-viewer-toolbar-separator"></div>

      <button hxButton variant="ghost" size="sm" (click)="copyToClipboard()" aria-label="Copy JSON to clipboard">
        <hx-icon svgIcon="copy" inline="true"></hx-icon>
        <span>Copy</span>
      </button>
    </div>
    @if (copied()) {
      <span class="hx-json-viewer-copy-notification">
        <hx-icon svgIcon="check-circle" inline="true"></hx-icon>
        Copied!
      </span>
    }
  </div>
}

<!-- Error message for invalid JSON -->
@if (errorMessage()) {
  <hx-alert variant="danger" appearance="subtle" [title]="'Invalid JSON'" [description]="errorMessage()"></hx-alert>
}

<!-- JSON Tree View -->
@if (!errorMessage() && viewMode() === 'tree') {
  <div class="hx-json-viewer" [class.hx-json-viewer--line-numbers]="showLineNumbers()">
    <ng-container *ngTemplateOutlet="nodeTemplate; context: { nodes: rootNodes(), indent: 0 }"></ng-container>
  </div>
}

<ng-template #nodeTemplate let-nodes="nodes" let-indent="indent">
  @for (node of nodes; track node.key + $index) {
    <div class="hx-json-node" [style.padding-left.px]="indent * indentSize()">
      <!-- Collapsible toggle for objects/arrays -->
      @if (isExpandable(node)) {
        <button
          type="button"
          class="hx-json-toggle"
          (click)="toggleNode(node)"
          [attr.aria-label]="node.collapsed ? 'Expand ' + node.key : 'Collapse ' + node.key"
          [attr.aria-expanded]="!node.collapsed"
          [hxTooltip]="node.collapsed ? 'Click to expand' : 'Click to collapse'"
          position="right">
          <i class="ccs" [class.ccs-chevron-right]="node.collapsed" [class.ccs-chevron-down]="!node.collapsed"></i>
          <span class="hx-json-toggle-hint">{{ node.collapsed ? 'expand' : 'collapse' }}</span>
        </button>
      } @else {
        <span class="hx-json-toggle-placeholder"></span>
      }

      <!-- Key -->
      <span class="hx-json-key">"{{ node.key }}"</span>
      <span class="hx-json-colon">:</span>

      <!-- Value based on type -->
      @switch (node.type) {
        @case ('object') {
          <span class="hx-json-bracket">{{ '{' }}</span>
          @if (node.collapsed) {
            <span class="hx-json-collapsed" (click)="toggleNode(node)">...</span>
            <span class="hx-json-bracket">{{ '}' }}</span>
            <span class="hx-json-count">({{ node.children?.length || 0 }} properties)</span>
          }
        }
        @case ('array') {
          <span class="hx-json-bracket">[</span>
          @if (node.collapsed) {
            <span class="hx-json-collapsed" (click)="toggleNode(node)">...</span>
            <span class="hx-json-bracket">]</span>
            <span class="hx-json-count">({{ node.children?.length || 0 }} items)</span>
          }
        }
        @case ('json-string') {
          <!-- valueString containing JSON - show as expandable object -->
          <span class="hx-json-bracket">{{ '{' }}</span>
          @if (node.collapsed) {
            <span class="hx-json-collapsed" (click)="toggleNode(node)">...</span>
            <span class="hx-json-bracket">{{ '}' }}</span>
          }
          <span class="hx-json-tag">JSON</span>
        }
        @case ('string') {
          <span class="hx-json-string">"{{ node.value }}"</span>
        }
        @case ('number') {
          <span class="hx-json-number">{{ node.value }}</span>
        }
        @case ('boolean') {
          <span class="hx-json-boolean">{{ node.value }}</span>
        }
        @case ('null') {
          <span class="hx-json-null">null</span>
        }
      }
    </div>

    <!-- Children (when expanded) -->
    @if (!node.collapsed && node.children && node.children.length > 0) {
      <ng-container
        *ngTemplateOutlet="nodeTemplate; context: { nodes: node.children, indent: indent + 1 }"></ng-container>

      <!-- Closing bracket -->
      <div class="hx-json-node" [style.padding-left.px]="indent * indentSize()">
        <span class="hx-json-toggle-placeholder"></span>
        @if (node.type === 'object' || node.type === 'json-string') {
          <span class="hx-json-bracket">{{ '}' }}</span>
        } @else if (node.type === 'array') {
          <span class="hx-json-bracket">]</span>
        }
      </div>
    }
  }
</ng-template>

<!-- Raw JSON View -->
@if (!errorMessage() && viewMode() === 'raw') {
  <pre class="hx-json-viewer-raw">{{ getRawJsonString() }}</pre>
}

<!-- Edit Mode -->
@if (viewMode() === 'edit' && editable()) {
  <div class="hx-json-viewer-editor">
    <textarea
      class="hx-json-viewer-editor__textarea"
      [value]="editorContent()"
      [placeholder]="placeholder()"
      aria-label="JSON editor"
      (input)="onEditorInput($event)"
      spellcheck="false"></textarea>
  </div>
}

<!-- Footer (edit mode) -->
@if (viewMode() === 'edit' && editable() && showFooter()) {
  <div class="hx-json-viewer-footer">
    <span class="hx-json-viewer-footer__info">Lines: {{ lineCount() }} | Characters: {{ characterCount() }}</span>
  </div>
}
